/* Copyright (c) 2017 Backpack Health LLC */

import DateUtil from '../../utilities/DateUtil';
import HealthEntryCategoryNames from '../../constants/HealthEntryCategoryNames';

const hasSharedComments = (comments) => {
  for (let j = 0; j < comments.length; j++) {
    if (comments[j].include_on_share_card) {
      return true;
    }
  }
  return false;
}

const getSharedComments = (comments) => {
  return comments.filter(c => c.include_on_share_card);
}

export function healthEntryPrototype() {
  return {
    data: () => ({}),
    id: null,
    category: '',
    current: {
      isCurrent: () => false,
      hasCurrent: () => false,
    },
    period: {
      isPeriod: () => false,
    },
    startDate: {},
    endDate: {},
    medliTerm: {},
    attachments: {
      hasAttachments: () => {
        const counts = this.data().attachment_count || { file: 0 };
        return counts.file ? counts.file > 0 : false;
      },
      counts: () => this.data().attachment_count || {},
    },
    comments: {
      getAll: () => this.data().comments,
      add: (value) => { this.data().comments.push(value); },
      hasComments: () => this.data().comments.length > 0,
      hasSharedComments: () => {
        return hasSharedComments(this.data().comments);
      },
      getSharedComments: () => getSharedComments(this.data().comments),
    },
  };
}

export function multiOccurrencePrototype() {
  // when looking at dates, attachments, flag, etc for a multiple occurence health entry,
  // we only want to look at the most recent health entry value

  return {
    flag: {
      get: () => this.values.mostRecent().flag.get(),
      set: () => {},
      toggle: () => {},
      isFlagged: () => this.values.mostRecent().flag.get(),
    },
    attachments: {
      hasAttachments: () => {
        const counts = this.data().attachment_count || { file: 0 };
        return counts.file ? counts.file > 0 : false;
      },
      counts: () => this.data().attachment_count || {},
    },
    comments: {
      getAll: () => this.data().comments,
      add: (value) => { this.data().comments.push(value); },
      hasComments: () => {
        const d = this.data();
        const values = d[d.category].values;
        for (let i = 0; i < values.length; i++) {
          if (values[i].comments.length > 0) {
            return true;
          }
        }
        return false;
      },
      getSharedComments: () => {},
      hasSharedComments: () => {
        const d = this.data();
        const values = d[d.category].values;
        for (let i = 0; i < values.length; i++) {
          const comments = values[i].comments;
          if (hasSharedComments(comments)) {
            return true;
          }
        }
        return false;
      },
    },
    dateUtil: {
      mostRecentDate: () => this.values.mostRecent().date.data(),
      isRecent: (earlyDate) => this.values.mostRecent().dateUtil.isRecent(earlyDate),
      endDateForSorting: () => this.values.mostRecent().date.date(),
      startDateForSorting: () => this.values.mostRecent().date.date(),
      isBefore: (otherHEValue) => (DateUtil.isBefore(this.values.mostRecent().date.date(), otherHEValue.dateUtil.mostRecentDate())),
      isOnlyPast: () => true,
    },
    values: {},
    sort: {},
  };
}

export function singleOccurrencePrototype() {
  return {
    dateUtil: {
      isRecent: (earlyDate) => {
        const startDateData = this.startDate.data();
        const endDateData = this.endDate.data();
        const hasStartDate = startDateData.date !== '';
        const hasEndDate = endDateData.date !== '';

        if (!hasStartDate && !hasEndDate) {
          return false;
        } else if (hasEndDate) {
          const maxEndDate = DateUtil.getMaxEndDate(endDateData);
          return !DateUtil.isBefore(maxEndDate, earlyDate);
        } else {
          const maxEndDate = DateUtil.getMaxEndDate(startDateData);
          return !DateUtil.isBefore(maxEndDate, earlyDate);
        }
      },
      endDateForSorting: () => {
        if (this.current.isCurrent()) {
          return DateUtil.today();
        } else {
          return this.endDate.hasDate() ? this.endDate.date() : '1600-01-01';
        }
      },
      startDateForSorting: () => (this.startDate.hasDate() ? this.startDate.date() : '1600–01–01'),
      isBefore: (otherHealthEntry) => {
        const thisStartDate = this.dateUtil.startDateForSorting();
        const otherStartDate = otherHealthEntry.dateUtil.startDateForSorting();
        const thisEndDate = this.dateUtil.endDateForSorting();
        const otherEndDate = otherHealthEntry.dateUtil.endDateForSorting();

        if (thisEndDate === otherEndDate) {
          if (thisStartDate === otherStartDate) {
            return this.medliTerm.formattedTermName() > otherHealthEntry.medliTerm.formattedTermName();
          } else {
            return DateUtil.isBefore(thisStartDate, otherStartDate);
          }
        } else {
          return DateUtil.isBefore(thisEndDate, otherEndDate);
        }
      },
      isOnlyPast: () => {
        switch (this.category) {
          case HealthEntryCategoryNames.vaccination:
          case HealthEntryCategoryNames.procedure:
          case HealthEntryCategoryNames.lab:
          case HealthEntryCategoryNames.measurement:
            return true;
          default:
            return false;
        }
      },
    },
    sort: {
      withAnotherHealthEntry: (otherHealthEntry) => {
        if (this.flag.isFlagged() && !otherHealthEntry.flag.isFlagged()) {
          return -1;
        } else if (!this.flag.isFlagged() && otherHealthEntry.flag.isFlagged()) {
          return 1;
        } else if (this.dateUtil.isBefore(otherHealthEntry)) {
          return 1;
        } else {
          return 0;
        }
      },
    },
  };
}

export function heValuePrototype() {
  return {
    dateUtil: {
      isRecent: (earlyDate) => {
        const dateData = this.date.data();
        const hasDate = dateData.date !== '';

        if (!hasDate) {
          return false;
        } else {
          const maxEndDate = DateUtil.getMaxEndDate(dateData);
          return !DateUtil.isBefore(maxEndDate, earlyDate);
        }
      },
      endDateForSorting: () => (this.date.hasDate() ? this.date.date() : '1600-01-01'),
      startDateForSorting: () => (this.date.hasDate() ? this.date.date() : '1600–01–01'),
      isBefore: (otherHEValue) => {
        const thisDate = this.dateUtil.startDateForSorting();
        const otherDate = otherHEValue.dateUtil.startDateForSorting();

        if (thisDate === otherDate) {
          if (thisDate === otherDate) {
            return this.medliTerm.formattedTermName() > otherHEValue.medliTerm.formattedTermName();
          } else {
            return DateUtil.isBefore(thisDate, otherDate);
          }
        } else {
          return DateUtil.isBefore(thisDate, otherDate);
        }
      },
      isOnlyPast: () => true,
    },
    attachments: {
      hasAttachments: () => {
        const counts = this.data().attachment_count || { file: 0 };
        return counts.file ? counts.file > 0 : false;
      },
      counts: () => this.data().attachment_count || {},
    },
    comments: {
      getAll: () => this.data().comments,
      add: (value) => { this.data().comments.push(value); },
      hasComments: () => this.data().comments.length > 0,
      hasSharedComments: () => {
        return hasSharedComments(this.data().comments);
      },
      getSharedComments: () => getSharedComments(this.data().comments),
    },
  };
}
